"use client";

import { motion, useInView } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import React, { useEffect, useRef, useState } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import useBreakPoint from "@/hooks/useBreakpoint";
import cn from "@/utils/cn";

import { DownloadMedia as DownloadMediaType } from "../UnitGallery/type";
import "./downloadMedia.css";

interface DownloadMediaProps {
  entry: DownloadMediaType;
  animation?: boolean;
  indexKey?: number;
}

const DownloadMedia: React.FC<DownloadMediaProps> = ({ entry, animation, indexKey }) => {
  const ButtonPDF = cn(
    "absolute w-full h-full left-0 top-0 flex justify-end pr-m md:relative md:w-full md:h-full md:btn md:btn-small md:h-xl md:btn-primary-white !border-white hover:!border-0 rounded-3xl md:border-0 md:shadow font-medium whitespace-nowrap inline-flex items-center",
  );
  const locale = useLocale();
  const t = useTranslations();
  const { isMd } = useBreakPoint("md");
  const componentRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(componentRef);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (isMd !== undefined) {
      const downloadMediaItems = document.querySelectorAll(".download-media-wrapper");
      const downloadSeparator = document.querySelectorAll(".download-separator");
      const addBorderClasses = () => {
        if (downloadMediaItems.length > 0) {
          downloadMediaItems[0]?.classList.add("rounded-t-2xl");
        }
      };
      const hideSeparator = () => {
        if (downloadMediaItems.length <= 2) {
          downloadSeparator[0]?.classList.add("hidden");
          downloadMediaItems[0]?.classList.add("pb-xxs");
        }
      };

      addBorderClasses();
      hideSeparator();
    }
  }, [isMd]);

  const fadeVariants = {
    visible: { opacity: 1, y: 0, transition: { delay: indexKey === 2 ? 1.5 : indexKey, duration: 1, ease: "easeInOut" } },
    hidden: { opacity: 0, y: 20 },
  };

  useEffect(() => {
    if (isInView && !hasAnimated && animation) {
      setHasAnimated(true);
    }
  }, [isInView, hasAnimated, animation]);

  return (
    <>
      <motion.div
        ref={componentRef}
        className="download-media-wrapper rounded-2xl mt-xl mx-m md:mx-auto bg-gray-7 md:w-1/2 px-m"
        initial={animation ? "hidden" : ""}
        animate={animation ? (hasAnimated ? "visible" : "hidden") : "visible"}
        variants={fadeVariants}
      >
        <div className="flex justify-between w-full pt-m pb-s relative max-md:pr-xxl border-b-[1px] border-b-gray-10">
          <div className="flex items-center gap-xs">
            <Icon size="20" name="document" className="stroke-black" />
            <label className="font-normal font-primary text-s uppercase">
              {entry.pdf.description || entry.pdf.title}
              {entry.date && <span className="font-secondary-light font-secondary mt-xxs text-xs flex normal-case">{entry.date}</span>}
            </label>
          </div>
          <div className="flex">
            <a target="_blank" href={entry.pdf.url} download className={ButtonPDF} aria-label={entry.downloadLabel ?? t("pdf.download")}>
              <>
                {!isMd && <Icon className="mr-xs max-md:stroke-black-0" name={"download.svg"} size="20" />}
                <span aria-hidden className="hidden md:inline-block">
                  {entry.downloadLabel ?? t("pdf.download")}
                </span>
              </>
            </a>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default DownloadMedia;
